// Avaliação IDS sobre um modelo carregado na plataforma.
//
// Tudo o que decide vereditos vive no motor partilhado em packages/ids,
// que o servidor MCP também usa. Este ficheiro é a ligação: dá ao motor
// uma implementação do contrato `IfcSource` assente no parser paralelo
// (rawIfc) e escolhe as mensagens em português.
//
// O contrato são duas funções síncronas. O rawIfc responde às duas de
// forma síncrona depois do modelo carregado (só o `loadModel` é
// assíncrono), e o `getEntity` já devolve as entidades com as
// referências resolvidas e com `type` como nome da classe IFC, que é
// exactamente o que o contrato exige. Daí isto ser tão fino.

import { rawIfc } from '@viewer/lib/rawIfc'
import {
  buildIdsIndexes as buildIdsIndexesCore,
  buildBcfTopicDrafts as buildBcfTopicDraftsCore,
  buildView as buildViewCore,
  checkRequirement as checkRequirementCore,
  evaluateIds as evaluateIdsCore,
  idsToCsv as idsToCsvCore,
  MESSAGES_PT,
  type BcfTopicDraft,
  type ElementView,
  type Facet,
  type FacetCheck,
  type IdsFile,
  type IdsReport,
  type IfcSource,
  type Indexes,
} from '@vn2r/ids'

export {
  matchValue,
  numbersClose,
  applicabilityMatches,
  buildIdsColorMap,
  readPropCells,
  IDS_TOLERANCE_EPSILON,
  type PropView,
  type ClassificationView,
  type ContainerView,
  type ElementView,
  type FacetCheck,
  type ElementFailure,
  type SpecResult,
  type IdsReport,
  type BcfTopicDraft,
  type PropCell,
  type Indexes,
  type IdsIndexes,
} from '@vn2r/ids'

/** O contrato do motor, servido pelo rawIfc para um modelo. */
function sourceFor(modelId: string): IfcSource {
  return {
    getLineIDsByType: (ifcTypeName) =>
      rawIfc.getLineIDsByType(modelId, ifcTypeName),
    getEntity: (expressID) =>
      rawIfc.getEntity(modelId, expressID) as Record<string, unknown> | null,
  }
}

/** Garante que o parser tem o modelo antes de o motor lhe fazer
 *  perguntas. É o único passo assíncrono de todo o processo. */
async function ensureLoaded(
  modelId: string,
  onProgress?: (step: string) => void,
): Promise<void> {
  if (rawIfc.hasModel(modelId)) return
  onProgress?.('A carregar o parser IFC...')
  await rawIfc.loadModel(modelId)
}

/** Avalia um facet de requisito. Razões de falha em português. */
export function checkRequirement(facet: Facet, view: ElementView): FacetCheck {
  return checkRequirementCore(facet, view, MESSAGES_PT)
}

/** Índices de propriedades, classificações, materiais, tipos e
 *  contentores para um modelo. */
export async function buildIdsIndexes(
  modelId: string,
  onProgress?: (step: string) => void,
): Promise<Indexes> {
  await ensureLoaded(modelId, onProgress)
  return buildIdsIndexesCore(sourceFor(modelId), onProgress, MESSAGES_PT)
}

/** Vista de um elemento, a partir dos índices já construídos. */
export function buildView(
  modelId: string,
  expressID: number,
  ifcClass: string,
  idx: Indexes,
): ElementView {
  return buildViewCore(sourceFor(modelId), expressID, ifcClass, idx)
}

/** Valida um modelo contra um ficheiro IDS. Relatório em português. */
export async function evaluateIds(
  modelId: string,
  ids: IdsFile,
  onProgress?: (step: string) => void,
): Promise<IdsReport> {
  await ensureLoaded(modelId, onProgress)
  return evaluateIdsCore(sourceFor(modelId), ids, onProgress, MESSAGES_PT)
}

/** Tópicos BCF a partir do relatório, em português. */
export function buildBcfTopicDrafts(report: IdsReport): BcfTopicDraft[] {
  return buildBcfTopicDraftsCore(report, MESSAGES_PT)
}

/** Relatório em CSV, com cabeçalhos em português. */
export function idsToCsv(report: IdsReport): string {
  return idsToCsvCore(report, MESSAGES_PT)
}
